<template>
  <section class="dc-finding-drawer" data-testid="finding-drawer">
    <p v-if="active">{{ $t('damage_control.review.draw_hint') }}</p>
    <div ref="canvas" class="dc-finding-canvas" :class="{ 'is-drawing': active && editable && !busy && !refreshNeeded }" data-testid="finding-canvas" @pointerdown="startDrawing" @pointermove="moveDrawing" @pointerup="finishDrawing" @pointercancel="cancelDrawing" @lostpointercapture="cancelDrawing">
      <img :src="imageUrl" :alt="$t('damage_control.review.title')" class="dc-finding-image" draggable="false" @error="$emit('preview-error', imageUrl)" />
      <div class="dc-finding-overlay"><slot name="overlay" :shot="shot" :zone-key="zoneKey" />
        <div v-if="draft" class="dc-finding-draft" :style="draftStyle" data-testid="finding-draft" />
      </div>
    </div>
    <v-alert v-if="errorText" type="warning" outlined role="alert" data-testid="finding-error">{{ errorText }}</v-alert>
    <v-btn v-if="refreshNeeded" text :disabled="busy" data-testid="refresh-inspection" @click="refreshInspection">{{ $t('damage_control.capture.refresh') }}</v-btn>
    <v-btn v-if="active && !draft" text :disabled="busy || disabled" data-testid="cancel-drawing-empty" @click="cancelSession">{{ $t('damage_control.review.cancel') }}</v-btn>
    <v-form v-if="active && draft" data-testid="finding-form" @submit.prevent="saveFinding">
      <v-select :disabled="disabled || busy" v-model="damageType" :items="damageTypes" item-text="text" item-value="value" :label="$t('damage_control.review.damage_type')" data-testid="damage-type" />
      <v-text-field :disabled="disabled || busy" v-model="severity" :label="$t('damage_control.review.severity')" data-testid="severity" />
      <v-radio-group :disabled="disabled || busy" v-model="classification" :label="$t('damage_control.review.classification')" data-testid="classification">
        <v-radio :label="$t('damage_control.review.classification_new')" value="new" data-testid="classification-new" />
        <v-radio :label="$t('damage_control.review.classification_known')" value="known" :disabled="!zoneEntries.length" data-testid="classification-known" />
      </v-radio-group>
      <v-select v-if="classification === 'known'" :disabled="disabled || busy" v-model="knownRecordId" :items="zoneEntries" item-text="label" item-value="id" :label="$t('damage_control.review.known_record')" data-testid="known-record" />
      <v-btn text :disabled="busy || disabled" data-testid="cancel-drawing-form" @click="cancelSession">{{ $t('damage_control.review.cancel') }}</v-btn>
      <v-btn color="primary" type="submit" :disabled="!canSave || busy" :loading="busy" data-testid="save-finding">{{ $t('damage_control.review.save_finding') }}</v-btn>
    </v-form>
  </section>
</template>

<script>
import { damageControlApi, reportDamageControlFailure } from '@/api/damageControl';
import { DAMAGE_TYPE_KEYS, damageTypeKey } from './presentation';
const clamp = (value) => Math.max(0, Math.min(1, value));

import { shotStillCurrent, zoneEntries } from "@/utils/damageControlReview";
import reviewRefreshMixin from "@/components/dashboard/damage_control/review/reviewRefreshMixin";

export default {
  name: 'FindingDrawer',
  mixins: [reviewRefreshMixin],
  reviewRefresh: { refreshKey: "refreshNeeded" },
  props: {
    inspection: { type: Object, required: true }, shot: { type: Object, required: true },
    zoneKey: { type: String, required: true }, entries: { type: Array, default: () => [] },
    active: { type: Boolean, default: false }, imageUrl: { type: String, default: '' }, disabled: { type: Boolean, default: false },
  },
  data() { return { draft: null, startPoint: null, pointerId: null, damageType: 'scratch', severity: '', classification: null, knownRecordId: null, findingSaved: false }; },
  computed: {
    editable() {
      return !this.disabled && this.inspection.status === 'in_progress' && this.shot.current !== false && !this.shot.superseded_by_id && this.shotStillCurrent;
    },
    shotStillCurrent() {
      return shotStillCurrent(this.inspection, this.zoneKey, this.shot, { coerceId: true });
    },
    zoneEntries() {
      return zoneEntries(this.entries, this.zoneKey, (key, params) => this.$t(key, params));
    },
    damageTypes() { return Object.keys(DAMAGE_TYPE_KEYS).map((value) => ({ value, text: this.$t(damageTypeKey(value)) })); },
    canSave() { return this.active && this.editable && Boolean(!this.findingSaved && !this.refreshNeeded && this.draft && DAMAGE_TYPE_KEYS[this.damageType] && (this.classification === 'new' || (this.classification === 'known' && this.zoneEntries.some((entry) => String(entry.id) === String(this.knownRecordId))))); },
    draftStyle() {
      if (!this.draft) return {};
      return { left: `${this.draft.x * 100}%`, top: `${this.draft.y * 100}%`, width: `${this.draft.w * 100}%`, height: `${this.draft.h * 100}%` };
    },
  },
  watch: {
    active(value) { if (!value) this.cancelSession(); },
    shot(next, previous) { if (next && previous && String(next.id) !== String(previous.id)) this.resetDraft(); },
    zoneKey(next, previous) { if (next !== previous) this.resetDraft(); },
    inspection() { if ((this.inspection.status !== 'in_progress' || !this.shotStillCurrent) && this.startPoint) this.cancelDrawing(); },
  },
  methods: {
    applyRefreshedInspection(inspection) {
      this.$emit('update:inspection', inspection); this.$emit('update:active', false);
      this.findingSaved = false; this.refreshNeeded = false; this.resetDraft();
    },
    refreshInspection() { return this.refresh({ force: true, clearBefore: true, retryOnError: true }); },
    startDrawing(event) {
      if (!this.active || !this.editable || this.busy || this.startPoint || this.draft || this.findingSaved || this.refreshNeeded || event.button !== 0 || event.isPrimary === false) return;
      const rect = this.$refs.canvas.getBoundingClientRect();
      if (!rect.width || !rect.height) return;
      event.preventDefault();
      if (this.$refs.canvas.setPointerCapture) this.$refs.canvas.setPointerCapture(event.pointerId);
      this.pointerId = event.pointerId;
      this.startPoint = { x: clamp((event.clientX - rect.left) / rect.width), y: clamp((event.clientY - rect.top) / rect.height) };
      this.draft = null;
    },
    moveDrawing(event) { if (this.startPoint && event.pointerId === this.pointerId) this.updateDraft(event); },
    updateDraft(event) {
      const rect = this.$refs.canvas.getBoundingClientRect();
      if (!rect.width || !rect.height) return;
      const point = { x: clamp((event.clientX - rect.left) / rect.width), y: clamp((event.clientY - rect.top) / rect.height) };
      // Округление и обрезка: сервер отклоняет рамку, у которой x + w или y + h больше 1 хоть на погрешность.
      const round = (value) => Math.round(value * 10000) / 10000;
      const x = round(Math.min(this.startPoint.x, point.x)); const y = round(Math.min(this.startPoint.y, point.y));
      const w = Math.min(round(Math.abs(point.x - this.startPoint.x)), round(1 - x));
      const h = Math.min(round(Math.abs(point.y - this.startPoint.y)), round(1 - y));
      this.draft = w > 0 && h > 0 ? { x, y, w, h } : null;
    },
    finishDrawing(event) {
      if (!this.startPoint || event.pointerId !== this.pointerId) return;
      this.updateDraft(event); this.startPoint = null; this.pointerId = null;
      if (this.draft && (this.draft.w < 0.005 || this.draft.h < 0.005)) this.draft = null;
      if (this.$refs.canvas.releasePointerCapture && this.$refs.canvas.hasPointerCapture && this.$refs.canvas.hasPointerCapture(event.pointerId)) this.$refs.canvas.releasePointerCapture(event.pointerId);
      this.damageType = 'scratch'; this.severity = ''; this.classification = null; this.knownRecordId = null;
    },
    cancelDrawing(event) {
      if (!this.startPoint || (event && event.pointerId !== this.pointerId)) return;
      this.startPoint = null; this.pointerId = null; this.draft = null;
    },
    cancelSession() { this.resetDraft(); this.$emit('update:active', false); },
    resetDraft() { this.startPoint = null; this.pointerId = null; this.draft = null; this.classification = null; this.knownRecordId = null; },
    async saveFinding() {
      if (!this.canSave || this.busy) return;
      this.busy = true; this.errorText = ''; this.refreshNeeded = false;
      const attrs = { bbox: this.draft, damage_type: this.damageType, classification: this.classification };
      if (this.severity.trim()) attrs.severity = this.severity.trim();
      if (this.classification === 'known') attrs.known_record_id = this.knownRecordId;
      try {
        await damageControlApi.drawFinding(this.axios, this.shot.id, attrs);
        this.findingSaved = true;
        await this.refreshInspection();
      } catch (error) {
        this.errorText = reportDamageControlFailure(this, error, { silent: true }) || this.$t(this.findingSaved ? 'damage_control.review.update_failed' : 'damage_control.review.draw_failed');
        if (this.findingSaved) this.refreshNeeded = true;
      }
      finally { this.busy = false; }
    },
  },
};
</script>

<style scoped>
.dc-finding-canvas { position: relative; width: 100%; touch-action: auto; user-select: none; }
.dc-finding-canvas.is-drawing { touch-action: none; }
.dc-finding-image { display: block; width: 100%; height: auto; }
.dc-finding-overlay { position: absolute; inset: 0; }
.dc-finding-draft { position: absolute; box-sizing: border-box; border: 2px solid #ff9800; background: rgba(255, 152, 0, .18); pointer-events: none; }
@media (max-width: 599px) { .dc-finding-drawer .v-btn { width: 100%; margin: 6px 0; } }
</style>
